// src/pages/HomePage.js - "의도된" 세로 레이아웃 버전

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { IconSpecial } from '../components/ui';
import './HomePage.scss';

// ReOpsAI 데이터
const USE_CASES_TITLE = [
  '리서치 방법론은 잘 모르지만 \n <0>리서치 기반으로 서비스를 개선</0>하고 싶을 때',
  '<0>전담 리서치팀 없이도</0> \n <1>대기업 수준의 리서치</1>를 진행하고 싶을 때',
  '초기 계획 수립과 리크루팅 등 \n <0>반복적인 업무를 자동화</0>하고 싶을 때',
];

const PAIN_POINTS_LIST = [
  '우리 서비스를 쓸만한\u00A0 \n <0>진짜 사용자</0>를 대체 어디서 찾나요?',
  '일정 조율, 보상 지급, 동의서... \n <0>시간이 부족</0>해서 도무지 엄두가 안나요',
  '<0>계획부터 막막</0>해요. \n 그래서 무엇을, 어떻게 물어봐야 하죠?',
  '<0>대상자 모집</0>은 업체에 맡기고 싶은데, \n 배보다 배꼽이 더 크네요...',
];

const HERO_TITLE = {
  title: 'Operation in a day, Insight in a week',
  desc: 'ReOpsAI는 설계부터 리크루팅까지 UX 리서치 준비의 전 과정을 지원하는 AI 도구입니다.',
};

const SOLUTION_LIST = ['문제 진단 및 계획 세우기', '유저 모집 준비하기', '참여자 선별하기', '참여자와 일정 조율하기', '인터뷰 대본 준비하기'];

const resolveEnterpriseLoginUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/login';
  }
  if (hostname === 'reopsai.com' || hostname === 'www.reopsai.com') {
    return 'https://b2b.reopsai.com/login';
  }
  return '/login';
};

const GUIDE_LIST = [
  {
    tab: '리서치 계획서 생성',
    title: '전문가 없이도 전문가처럼, \n 23년 노하우를 빌려 리서치 계획 세우기',
    description:
      'UX 리서치에 대한 전문 지식이나 경험이 없어도 괜찮아요.\n pxd 노하우가 집약된 DB를 활용하면, 누구나 쉽게 상황에 맞는 최적화된 조사를 설계할 수 있어요. \n ReOpsAI를 통해 탄탄하고 정확한 리서치 설계를 완성하고 전문적 리서치의 첫걸음을 내딛으세요.',
    guide: [
      { text: 'pxd의 노하우가 집약된 DB에서 \n유사한 사례와 지식을 찾고 \n전문적인 리서치 계획서를 생성해요', image: require('../assets/images/reops_guide1_1.webp') },
      { text: '조사 목적에 맞춰 필수 정보와 \n 원하는 조건만 간단히 입력하세요', image: require('../assets/images/reops_guide1_2.webp') },
      { text: 'AI 어시스턴트와 함께 실시간으로 수정하고 \n 원하는 결과물을 재생성할 수 있어요', image: require('../assets/images/reops_guide1_3.webp') },
    ],
  },
  {
    tab: '스크리너 생성 & 가이드라인 생성',
    title: '어려운 설계 과정을 클릭 한번에 \n 스크리닝 설문과 가이드라인 자동 생성하기',
    description:
      '실질적인 사용자 인사이트를 얻기 위해서는 연구 목적에 적합한 모집과 질문이 필요해요. \n 그러나 스크리닝 설문과 진행 가이드라인 설계는 전문적인 지식이 필요한 어려운 과정입니다. \n ReOpsAI는 생성된 계획서를 바탕으로 최적의 스크리닝 설문과 진행 가이드라인을 버튼 클릭 한번에 생성하여 리서치 준비 과정을 획기적으로 단축시켜요.',
    guide: [
      { text: '단 한번의 클릭으로 리서치에 \n 필요한 각종 준비물을 정교하게 생성해요', image: require('../assets/images/reops_guide2_1.webp') },
      { text: '모집 기준에 적합한 핵심 선별 변수를 \n 바탕으로 체계적인 스크리닝 설문을 설계해요', image: require('../assets/images/reops_guide2_2.webp') },
      { text: '사용자에게 집중해야 할 관찰 포인트와 \n 질문 목록을 담은 종합 가이드라인을 제공해요.', image: require('../assets/images/reops_guide2_3.webp') },
    ],
  },
  {
    tab: '참여자 선별',
    title: '맵핑된 변수 기반의 정교한 로직으로 \n 정확하게 참여자 선별하기',
    description:
      '까다롭고 번거로웠던 참여자 선별 및 일정 조율 과정을 ReOpsAI가 대신합니다.\n ReOpsAI의 참여자 선별기는 응답 성실도 및 핵심 변수 맵핑을 기반으로 정교한 선별 로직을 생성합니다.\n 이를 바탕으로 적합한 사용자를 선별하고, 인터뷰 일정을 자동으로 생성해 드립니다.',
    guide: [
      { text: '조사 계획서를 바탕으로 \n 조사 대상, 주요 변수를 분석해요', image: require('../assets/images/reops_guide3_1.webp') },
      { text: '핵심,균형 변수와 문항 맵핑을 기반으로 \n 정교한 선별 로직을 생성해요', image: require('../assets/images/reops_guide3_2.webp') },
      { text: '스코어링 점수와 균형변수의 분포를 고려하여 \n 참여자를 선별하고 일정을 생성해요', image: require('../assets/images/reops_guide3_3.webp') },
    ],
  },
];

// 유틸 함수
const parseTextWithStrong = (text) => {
  const regex = /<(\d+)>(.*?)<\/\1>/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    parts.push(<strong key={key++}>{match[2]}</strong>);
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

// 미디어 쿼리 훅
const useMediaQuery = (type) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    let mediaQueryString;
    switch (type) {
      case 'desktop':
        mediaQueryString = '(min-width: 1280px)';
        break;
      case 'tablet':
        mediaQueryString = '(min-width: 768px) and (max-width: 1279px)';
        break;
      case 'mobile':
      default:
        mediaQueryString = '(max-width: 767px)';
        break;
    }

    const mediaQueryList = window.matchMedia(mediaQueryString);
    const updateMatches = () => {
      setMatches(mediaQueryList.matches);
    };

    updateMatches();
    mediaQueryList.addEventListener('change', updateMatches);

    return () => {
      mediaQueryList.removeEventListener('change', updateMatches);
    };
  }, [type]);

  return matches;
};

// 드래그 스크롤 훅
const useDragScroll = () => {
  const scrollContainerRef = useRef(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const dragStateRef = useRef({ startX: 0, scrollLeft: 0 });

  const handleMouseDown = useCallback((e) => {
    if (!scrollContainerRef.current) return;
    setIsMouseDown(true);
    dragStateRef.current.startX = e.pageX - scrollContainerRef.current.offsetLeft;
    dragStateRef.current.scrollLeft = scrollContainerRef.current.scrollLeft;
    document.body.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isMouseDown || !scrollContainerRef.current) return;
      e.preventDefault();
      const x = e.pageX - scrollContainerRef.current.offsetLeft;
      const walk = (x - dragStateRef.current.startX) * 1;
      scrollContainerRef.current.scrollLeft = dragStateRef.current.scrollLeft - walk;
    },
    [isMouseDown],
  );

  const handleMouseUp = useCallback(() => {
    setIsMouseDown(false);
    document.body.style.userSelect = '';
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsMouseDown(false);
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const handleTouchStart = (e) => {
      setIsMouseDown(true);
      dragStateRef.current.startX = e.touches[0].clientX;
      dragStateRef.current.scrollLeft = el.scrollLeft;
    };

    const handleTouchMove = (e) => {
      if (!isMouseDown) return;
      e.preventDefault();
      const x = e.touches[0].clientX;
      const walk = dragStateRef.current.startX - x;
      el.scrollLeft = dragStateRef.current.scrollLeft + walk;
    };

    const handleTouchEnd = () => {
      setIsMouseDown(false);
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMouseDown]);

  return {
    scrollContainerRef,
    isMouseDown,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
  };
};

const HomePage = () => {
  const navigate = useNavigate();
  const { isLoggedIn, sessionChecked } = useAppContext();
  const isMobile = useMediaQuery('mobile');
  const [activeTab, setActiveTab] = useState('tab0');
  
  // 탭 드래그 스크롤
  const tabScroll = useDragScroll();
  // 가이드 리스트 드래그 스크롤
  const guideScroll = useDragScroll();

  useEffect(() => {
    if (!sessionChecked || !isLoggedIn) return;
    navigate('/b2b', { replace: true });
  }, [sessionChecked, isLoggedIn, navigate]);

  const handleDemoClick = () => {
    if (isLoggedIn) {
      navigate('/b2b');
    } else {
      navigate('/individual/login');
    }
  };

  // 탭 키보드 네비게이션
  const handleTabKeyDown = useCallback((e) => {
    if (e.target instanceof HTMLButtonElement) {
      const buttons = Array.from(e.currentTarget.querySelectorAll('button:not([disabled])'));
      const currentIndex = buttons.indexOf(e.target);
      let nextIndex = currentIndex;

      if (e.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % buttons.length;
      } else if (e.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
      }

      if (nextIndex !== currentIndex) {
        buttons[nextIndex].click();
        buttons[nextIndex].focus();
      }
    }
  }, []);

  const handleTabMouseDown = useCallback(
    (e) => {
      if (e.target instanceof HTMLButtonElement && e.target.disabled) {
        e.preventDefault();
      }
      tabScroll.handleMouseDown(e);
    },
    [tabScroll],
  );

  return (
    <div className="homepage">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="logo">
            <IconSpecial name="logo" className="homepage-logo" />
          </div>
          <nav className="nav">
            <Link to="/individual/login" className="login-button">로그인</Link>
            <a href={resolveEnterpriseLoginUrl()} className="login-button login-button--enterprise">
              기업 로그인
            </a>
          </nav>
        </div>
      </header>

      {/* ReOpsAI Section */}
      <div className="intro-reops-container">
        {/* ReOpsAI Hero */}
        <section className="intro-reops-hero">
          <div className="intro-reops-hero-inner">
            <div className="intro-reops-hero-content-wrap">
              <div className="intro-reops-hero-text-group">
                <h2 className="intro-reops-title">{HERO_TITLE.title}</h2>
                <p className="intro-reops-desc">{HERO_TITLE.desc}</p>
              </div>
              <button 
                onClick={handleDemoClick} 
                className="hero-cta-button"
              >
                3분 만에 리서치 설계하기
              </button>
            </div>
            <div className="intro-reops-image-wrap">
              <img src={require('../assets/images/reops_image.webp')} alt="ReOpsAI 미리보기 이미지" />
            </div>
          </div>
        </section>

        {/* Pain Points */}
        <section className="intro-reops-pain-points">
          <div className="intro-reops-inner">
            <div className="intro-reops-title-container">
              <span className="intro-reops-title-flag">PROBLEM</span>
              <h2 className="intro-reops-title-text">듣기 어려운 실제 사용자의 목소리</h2>
              <p className="intro-reops-title-description">왜 많은 조직은 사용자의 목소리를 듣는 걸 어려워할까요?</p>
            </div>
            <div className="intro-reops-pain-points-list">
              {PAIN_POINTS_LIST.map((item, index) => (
                <div className="intro-reops-pain-points-item" key={index}>
                  {parseTextWithStrong(item)}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Solution */}
        <section className="intro-reops-solution">
          <div className="intro-reops-inner">
            <div className="intro-reops-title-container">
              <span className="intro-reops-title-flag">SOLUTION</span>
              <h2 className="intro-reops-title-text">ReOpsAI는 UX 리서치의 복잡한 준비 과정을 효율화해요</h2>
              <p className="intro-reops-title-description">ReOpsAI는 누구나 쉽고 빠르게 UX 리서치를 반복하고, 민첩하게 인사이트를 얻을 수 있도록 지원해요.</p>
            </div>
            <div className="intro-reops-solution-wrap">
              <div className="intro-reops-solution-inner">
                <dl className="intro-reops-solution-asis-period">
                  <dt>UX 리서치 준비 전체 과정</dt>
                  <dd>1~2주 소요</dd>
                </dl>
                <div className="intro-reops-solution-tobe-wrap">
                  <div className="intro-reops-solution-tobe-list">
                    {SOLUTION_LIST.map((item, index) => (
                      <div className="intro-reops-solution-item" key={index}>
                        {item}
                      </div>
                    ))}
                  </div>
                  <dl className="intro-reops-solution-tobe-is">
                    <dt>ReOpsAI</dt>
                    <dd>1일 소요</dd>
                  </dl>
                  <div className="intro-reops-effect-wrap">
                    <strong className="intro-reops-effect-title">
                      UX 리서치 준비 기간 80% 이상 감소 <FontAwesomeIcon icon={faArrowDown} size={isMobile ? 16 : 20} />
                    </strong>
                  </div>
                </div>
              </div>
              <p className="intro-reops-solution-desc">
                리서치 준비 기간을 획기적으로 줄여, 사용자의 경험을 이해하는 데 집중할 수 있습니다.
                <br className="intro-reops-br" /> 깊이 있는 공감을 바탕으로 서비스의 개선과 개발에 필요한 시간을 즉각적으로 확보하세요.
              </p>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className="intro-reops-tab-wrap">
          <div className="intro-reops-tab-inner">
            <div className="intro-reops-tabs">
              <div className="intro-reops-tabs-list-wrap">
                <div
                  ref={tabScroll.scrollContainerRef}
                  className={`intro-reops-tabs-list ${tabScroll.isMouseDown ? 'grab' : ''}`}
                  role="tablist"
                  onKeyDown={handleTabKeyDown}
                  onMouseDownCapture={handleTabMouseDown}
                  onMouseMove={tabScroll.handleMouseMove}
                  onMouseUp={tabScroll.handleMouseUp}
                  onMouseLeave={tabScroll.handleMouseLeave}
                >
                  {GUIDE_LIST.map((guide, index) => (
                    <button
                      key={`tab-button-${index}`}
                      className={`intro-reops-tab-button ${activeTab === `tab${index}` ? 'active' : ''}`}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === `tab${index}`}
                      aria-controls={`intro-reops-tabpanel-tab${index}`}
                      tabIndex={activeTab === `tab${index}` ? 0 : -1}
                      onClick={() => setActiveTab(`tab${index}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setActiveTab(`tab${index}`);
                        }
                      }}
                    >
                      {guide.tab}
                    </button>
                  ))}
                </div>
              </div>

              {GUIDE_LIST.map((content, index) => {
                if (activeTab !== `tab${index}`) return null;
                return (
                  <div
                    key={`tab-content-${index}`}
                    id={`intro-reops-tabpanel-tab${index}`}
                    role="tabpanel"
                    aria-labelledby={`intro-reops-tab-tab${index}`}
                    className="intro-reops-tab-content"
                  >
                    <div className="intro-reops-guide-title-wrap">
                      <h2 className="intro-reops-guide-title">{content.title}</h2>
                      <p className="intro-reops-guide-description">{content.description}</p>
                    </div>
                    <div
                      ref={guideScroll.scrollContainerRef}
                      className={`intro-reops-tab-guide-list ${guideScroll.isMouseDown ? 'grab' : ''}`}
                      onMouseDown={guideScroll.handleMouseDown}
                      onMouseMove={guideScroll.handleMouseMove}
                      onMouseUp={guideScroll.handleMouseUp}
                      onMouseLeave={guideScroll.handleMouseLeave}
                    >
                      {content.guide.map((guide, guideIndex) => (
                        <div className="intro-reops-tab-guide-item" key={`tab-guide-${index}-${guideIndex}`}>
                          <div className="intro-reops-tab-guide-image-wrap">
                            <img src={guide.image} alt=" " />
                          </div>
                          <p className="intro-reops-tab-guide-text">{guide.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Use Case */}
        <section className="intro-reops-usecase">
          <div className="intro-reops-inner">
            <h2 className="intro-reops-usecase-title">
              ReOpsAI는 이런 상황에서 <br className="intro-reops-br" /> 유용하게 활용할 수 있어요
            </h2>
            <div className="intro-reops-usecase-list">
              {USE_CASES_TITLE.map((title, index) => (
                <div className="intro-reops-usecase-item" key={index}>
                  {parseTextWithStrong(title)}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Data Security */}
      <section className="security-section">
        <div className="container">
          <h2 className="section-title">Data Security</h2>
          <p className="security-desc">
            사용자의 데이터는 안전하게 보호됩니다. OpenAI, Google 등 신뢰할 수 있는 LLM API의 Zero-Training(비학습) 정책을 적용하여, 입력하신 데이터는 AI 모델 학습에 절대 사용되지 않습니다. 분석 기록은 서비스 제공 및 이력 관리를 위해 안전한 서버에 보관되며, 원하실 때 언제든 삭제 가능합니다.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <IconSpecial name="logo" className="footer-logo-icon" />
            </div>
            <div className="footer-links">
              <Link to="/terms">이용약관</Link>
              <Link to="/privacy">개인정보/데이터처리방침</Link>
            </div>
            <div className="footer-copy">
              <p>ReOpsAI © 2025</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
